import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { KeyRound, Plus, UserCheck, UserX } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ErroApi } from '../../api/cliente';
import { usuarios as apiUsuarios } from '../../api/endpoints';
import type { Perfil, Usuario } from '../../api/tipos';
import { Aviso } from '../../components/Aviso';
import { Botao } from '../../components/Botao';
import { Campo, Selecao } from '../../components/Campo';
import { Dialogo } from '../../components/Dialogo';
import { Carregando, Pagina } from '../../components/Pagina';
import { formatarDataHora } from '../../lib/formatos';
import { useSessao } from '../../hooks/useSessao';

const CHAVE = ['admin', 'usuarios'];

const senhaTemporaria = z.string().min(8, 'A senha precisa ter pelo menos 8 caracteres.').max(72);

const esquemaCriar = z.object({
  nome: z.string().trim().min(2, 'Informe o nome completo.'),
  login: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9._-]{3,40}$/, 'De 3 a 40 caracteres: letras sem acento, números, ponto, hífen ou sublinhado.'),
  perfil: z.enum(['USUARIO', 'ADMIN']),
  senhaTemporaria,
});

const esquemaEditar = z.object({
  nome: z.string().trim().min(2, 'Informe o nome completo.'),
  perfil: z.enum(['USUARIO', 'ADMIN']),
});

/** Senha temporária aleatória e legível (sem caracteres ambíguos como 0/O e 1/l). */
function gerarSenha(): string {
  const alfabeto = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789';
  const bytes = crypto.getRandomValues(new Uint8Array(10));
  return Array.from(bytes, (b) => alfabeto[b % alfabeto.length]).join('');
}

function rotuloPerfil(p: Perfil) {
  return p === 'ADMIN' ? 'Administrador' : 'Usuário';
}

type Acao = { tipo: 'criar' } | { tipo: 'editar'; usuario: Usuario } | { tipo: 'senha'; usuario: Usuario } | null;

export function Usuarios() {
  const cliente = useQueryClient();
  const { data: sessao } = useSessao();
  const { data, isPending, error } = useQuery({ queryKey: CHAVE, queryFn: apiUsuarios.listar });
  const [acao, setAcao] = useState<Acao>(null);
  const [mensagem, setMensagem] = useState<{ tipo: 'ok' | 'erro'; texto: string } | null>(null);

  const alternarAtivo = useMutation({
    mutationFn: (u: Usuario) => apiUsuarios.editar(u.id, { ativo: !u.ativo }),
    onSuccess: ({ usuario }) => {
      setMensagem({ tipo: 'ok', texto: `Acesso de ${usuario.nome} ${usuario.ativo ? 'reativado' : 'desativado'}.` });
      void cliente.invalidateQueries({ queryKey: CHAVE });
    },
    onError: (e) => setMensagem({ tipo: 'erro', texto: e instanceof ErroApi ? e.message : 'Não foi possível alterar o usuário.' }),
  });

  function concluir(texto: string) {
    setAcao(null);
    setMensagem({ tipo: 'ok', texto });
    void cliente.invalidateQueries({ queryKey: CHAVE });
  }

  return (
    <Pagina
      titulo="Usuários"
      contexto="Quem pode entrar no sistema. Usuários novos entram com senha temporária e criam a própria no primeiro acesso."
      acoes={
        <Botao variante="principal" onClick={() => setAcao({ tipo: 'criar' })}>
          <Plus aria-hidden className="size-4" /> Novo usuário
        </Botao>
      }
    >
      {mensagem && (
        <Aviso tipo={mensagem.tipo} className="mb-4 max-w-3xl">
          {mensagem.texto}
        </Aviso>
      )}
      {isPending ? (
        <Carregando />
      ) : error ? (
        <Aviso tipo="erro">{error.message}</Aviso>
      ) : (
        <div className="overflow-x-auto rounded-painel border border-linha bg-superficie">
          <table className="w-full min-w-[760px] border-collapse text-left">
            <thead className="border-b border-linha text-[13px] text-tinta-2">
              <tr>
                <th className="px-4 py-2.5 font-medium">Nome</th>
                <th className="px-4 py-2.5 font-medium">Login</th>
                <th className="px-4 py-2.5 font-medium">Perfil</th>
                <th className="px-4 py-2.5 font-medium">Situação</th>
                <th className="px-4 py-2.5 font-medium">Último acesso</th>
                <th className="px-4 py-2.5 font-medium">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.usuarios.map((u) => {
                const souEu = u.id === sessao?.usuario.id;
                return (
                  <tr key={u.id} className={clsx('border-b border-linha-suave last:border-0', !u.ativo && 'text-tinta-2')}>
                    <td className="px-4 py-2">
                      <button
                        type="button"
                        onClick={() => setAcao({ tipo: 'editar', usuario: u })}
                        className="cursor-pointer text-left font-medium text-acao hover:underline"
                      >
                        {u.nome}
                      </button>
                      {souEu && <span className="ml-2 text-[13px] text-tinta-2">(você)</span>}
                    </td>
                    <td className="px-4 py-2 font-mono text-[13px]">{u.login}</td>
                    <td className="px-4 py-2">{rotuloPerfil(u.perfil)}</td>
                    <td className="px-4 py-2">
                      {!u.ativo ? 'Inativo' : u.deveTrocarSenha ? <span className="text-aviso">Aguardando troca de senha</span> : 'Ativo'}
                    </td>
                    <td className="numero px-4 py-2 whitespace-nowrap">{formatarDataHora(u.ultimoLoginEm)}</td>
                    <td className="px-4 py-2">
                      <div className="flex justify-end gap-1">
                        <Botao tamanho="compacto" variante="discreto" onClick={() => setAcao({ tipo: 'senha', usuario: u })}>
                          <KeyRound aria-hidden className="size-3.5" /> Redefinir senha
                        </Botao>
                        {!souEu && (
                          <Botao
                            tamanho="compacto"
                            variante="discreto"
                            carregando={alternarAtivo.isPending && alternarAtivo.variables?.id === u.id}
                            onClick={() => alternarAtivo.mutate(u)}
                          >
                            {u.ativo ? <UserX aria-hidden className="size-3.5" /> : <UserCheck aria-hidden className="size-3.5" />}
                            {u.ativo ? 'Desativar' : 'Reativar'}
                          </Botao>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialogo aberto={acao?.tipo === 'criar'} titulo="Novo usuário" aoFechar={() => setAcao(null)}>
        {acao?.tipo === 'criar' && <FormCriar aoConcluir={(u) => concluir(`Cadastro de ${u.nome} criado. Passe a senha temporária para a pessoa.`)} aoCancelar={() => setAcao(null)} />}
      </Dialogo>
      <Dialogo aberto={acao?.tipo === 'editar'} titulo="Editar usuário" aoFechar={() => setAcao(null)}>
        {acao?.tipo === 'editar' && (
          <FormEditar usuario={acao.usuario} aoConcluir={(u) => concluir(`Cadastro de ${u.nome} atualizado.`)} aoCancelar={() => setAcao(null)} />
        )}
      </Dialogo>
      <Dialogo
        aberto={acao?.tipo === 'senha'}
        titulo="Redefinir senha"
        descricao={acao?.tipo === 'senha' ? `${acao.usuario.nome} (${acao.usuario.login})` : undefined}
        aoFechar={() => setAcao(null)}
      >
        {acao?.tipo === 'senha' && (
          <FormSenha usuario={acao.usuario} aoConcluir={(u) => concluir(`Senha de ${u.nome} redefinida. Uma senha própria será criada no próximo acesso.`)} aoCancelar={() => setAcao(null)} />
        )}
      </Dialogo>
    </Pagina>
  );
}

function RodapeForm({ enviando, textoBotao, aoCancelar }: { enviando: boolean; textoBotao: string; aoCancelar: () => void }) {
  return (
    <div className="flex justify-end gap-2 border-t border-linha-suave pt-4">
      <Botao variante="discreto" onClick={aoCancelar}>
        Cancelar
      </Botao>
      <Botao type="submit" variante="principal" carregando={enviando}>
        {textoBotao}
      </Botao>
    </div>
  );
}

function CampoSenhaTemporaria({ registro, erro, aoGerar }: { registro: ReturnType<ReturnType<typeof useForm>['register']>; erro?: string; aoGerar: () => void }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-end gap-2">
        <Campo rotulo="Senha temporária" autoComplete="off" spellCheck={false} className="flex-1 [&_input]:font-mono" erro={erro} {...registro} />
        {/* Com erro, o texto aparece abaixo do campo: o botão fica alinhado à caixa. */}
        <Botao variante="secundario" onClick={aoGerar} className={erro ? 'mb-[24px] h-9' : 'h-9'}>
          Gerar
        </Botao>
      </div>
      <p className="text-[13px] leading-[18px] text-tinta-2">
        Passe esta senha para quem vai usar a conta; a senha própria é criada no primeiro acesso.
      </p>
    </div>
  );
}

function FormCriar({ aoConcluir, aoCancelar }: { aoConcluir: (u: Usuario) => void; aoCancelar: () => void }) {
  const [erro, setErro] = useState<string | null>(null);
  const { register, handleSubmit, formState, setValue, setError } = useForm<z.infer<typeof esquemaCriar>>({
    resolver: zodResolver(esquemaCriar),
    defaultValues: { perfil: 'USUARIO', senhaTemporaria: gerarSenha() },
  });

  async function enviar(d: z.infer<typeof esquemaCriar>) {
    setErro(null);
    try {
      aoConcluir((await apiUsuarios.criar(d)).usuario);
    } catch (e) {
      if (e instanceof ErroApi && e.doCampo('login')) setError('login', { message: e.message });
      else setErro(e instanceof ErroApi ? e.message : 'Não foi possível cadastrar.');
    }
  }

  return (
    <form onSubmit={handleSubmit(enviar)} noValidate className="flex flex-col gap-4">
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      <Campo rotulo="Nome" autoFocus erro={formState.errors.nome?.message} {...register('nome')} />
      <div className="grid grid-cols-2 gap-3">
        <Campo rotulo="Login" autoComplete="off" spellCheck={false} erro={formState.errors.login?.message} {...register('login')} />
        <Selecao rotulo="Perfil" {...register('perfil')}>
          <option value="USUARIO">Usuário</option>
          <option value="ADMIN">Administrador</option>
        </Selecao>
      </div>
      <CampoSenhaTemporaria
        registro={register('senhaTemporaria')}
        erro={formState.errors.senhaTemporaria?.message}
        aoGerar={() => setValue('senhaTemporaria', gerarSenha(), { shouldValidate: true })}
      />
      <RodapeForm enviando={formState.isSubmitting} textoBotao="Cadastrar usuário" aoCancelar={aoCancelar} />
    </form>
  );
}

function FormEditar({ usuario, aoConcluir, aoCancelar }: { usuario: Usuario; aoConcluir: (u: Usuario) => void; aoCancelar: () => void }) {
  const [erro, setErro] = useState<string | null>(null);
  const { register, handleSubmit, formState } = useForm<z.infer<typeof esquemaEditar>>({
    resolver: zodResolver(esquemaEditar),
    defaultValues: { nome: usuario.nome, perfil: usuario.perfil },
  });

  async function enviar(d: z.infer<typeof esquemaEditar>) {
    setErro(null);
    try {
      aoConcluir((await apiUsuarios.editar(usuario.id, d)).usuario);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível salvar.');
    }
  }

  return (
    <form onSubmit={handleSubmit(enviar)} noValidate className="flex flex-col gap-4">
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      <Campo rotulo="Login" value={usuario.login} disabled readOnly ajuda="O login não pode ser alterado." />
      <Campo rotulo="Nome" autoFocus erro={formState.errors.nome?.message} {...register('nome')} />
      <Selecao rotulo="Perfil" {...register('perfil')}>
        <option value="USUARIO">Usuário</option>
        <option value="ADMIN">Administrador</option>
      </Selecao>
      <RodapeForm enviando={formState.isSubmitting} textoBotao="Salvar alterações" aoCancelar={aoCancelar} />
    </form>
  );
}

function FormSenha({ usuario, aoConcluir, aoCancelar }: { usuario: Usuario; aoConcluir: (u: Usuario) => void; aoCancelar: () => void }) {
  const [erro, setErro] = useState<string | null>(null);
  const esquema = z.object({ senhaTemporaria });
  const { register, handleSubmit, formState, setValue } = useForm<z.infer<typeof esquema>>({
    resolver: zodResolver(esquema),
    defaultValues: { senhaTemporaria: gerarSenha() },
  });

  async function enviar(d: z.infer<typeof esquema>) {
    setErro(null);
    try {
      aoConcluir((await apiUsuarios.redefinirSenha(usuario.id, d.senhaTemporaria)).usuario);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível redefinir a senha.');
    }
  }

  return (
    <form onSubmit={handleSubmit(enviar)} noValidate className="flex flex-col gap-4">
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      <p className="text-tinta-2">As sessões abertas deste usuário serão encerradas.</p>
      <CampoSenhaTemporaria
        registro={register('senhaTemporaria')}
        erro={formState.errors.senhaTemporaria?.message}
        aoGerar={() => setValue('senhaTemporaria', gerarSenha(), { shouldValidate: true })}
      />
      <RodapeForm enviando={formState.isSubmitting} textoBotao="Redefinir senha" aoCancelar={aoCancelar} />
    </form>
  );
}
